import { create } from 'zustand';
import { Element, ModeType, Quest, ElementType } from '@/constant/schema';
import useAudioStore from '@/store/useAudioStore';
import { stripNonTextCharacters } from '@/lib/utils';
import { v4 as uuidv4 } from 'uuid';
import useAuthStore from '@/store/useAuthStore';
import supabase from '@/lib/supabase';
import { getWelcomeMessage } from '@/lib/questHelper';
import useConfigStore from './useConfigStore';
import { AccessElementTypes, InteractiveElementTypes } from '@/constant/element-type-group';
import { useChatStore } from './useChatStore';

// Define message types
export type MessageRole = 'user' | 'assistant' | 'system';
export type MessageType = 'element' | 'text' | 'new_lesson';

export interface SuggestedQuestion {
  question: string;
  answer: string;
}

export interface MessageElement {
  element: Element;
  suggested_questions?: SuggestedQuestion[];
}

export interface MessageEvent {
  id: string;
  title: string;
}

export interface Message {
  id?: string; // message id
  user_id?: string; // user id
  quest_id?: string; // quest id
  chapter_id?: string; // chapter id
  lesson_id?: string; // lesson id
  element_id?: string; // element id
  role: MessageRole;
  type: MessageType;
  content?: string;
  element?: Element;
  event?: MessageEvent;
  created_at?: string; // timestamp
  isStreaming?: boolean;
}

interface MessageStore {
  messages: Message[];
  pendingMessages: Message[];
  currentQuestId: string;
  currentChapterId: string;
  currentLessonId: string;
  mode: ModeType;

  // Core actions
  addMessage: (message: Message) => Promise<void>;
  loadMessages: (questId: string, chapterId: string, lessonId: string, quest: Quest, mode: ModeType) => Promise<void>;
  setContext: (questId: string, chapterId: string) => void;
  hasElementBeenShown: (elementId: string) => boolean;
  isLastMessage: (messageId: string) => boolean;
  clearAndReinitialize: (questId: string, chapterId: string, lessonId: string, quest: Quest, mode: ModeType) => Promise<void>;
}

export const useMessageStore = create<MessageStore>((set, get) => ({
  messages: [],
  pendingMessages: [],
  currentQuestId: '',
  currentChapterId: '',
  currentLessonId: '',
  mode: ModeType.CHAT,
  
  setContext: (questId: string, chapterId: string) => {
    set({ currentQuestId: questId, currentChapterId: chapterId });
  },
  
  addMessage: async (messageData: Message) => {
    const { role, type, content, element } = messageData;
    const user_id = useAuthStore.getState().user?.id;
    // uuid
    messageData.id = uuidv4();
    messageData.user_id = user_id;
    messageData.quest_id = get().currentQuestId;
    messageData.chapter_id = get().currentChapterId;
    messageData.lesson_id = get().currentLessonId;
    messageData.created_at = new Date().toISOString();

    // Only save to database in non-review mode
    const reviewMode = useChatStore.getState().reviewMode;

    if (!reviewMode && user_id) {
      try {
        const { error } = await supabase
          .from('chat_message')
          .insert([{
            id: messageData.id,
            user_id: messageData.user_id,
            quest_id: messageData.quest_id,
            chapter_id: messageData.chapter_id,
            lesson_id: messageData.lesson_id,
            element_id: messageData.element?.id,
            role: messageData.role,
            type: messageData.type,
            content: messageData.content,
            event: messageData.event,
            created_at: messageData.created_at,
            mode_type: get().mode || ModeType.CHAT
          }]);

        if (error) {
          console.error('Error saving message to Supabase:', error);
        }
      } catch (error) {
        console.error('Failed to save message to Supabase:', error);
      }
    } else {
      console.log(user_id ? '🔄 Review mode: skipping message sync to database' : '👤 Guest mode: skipping message sync to database');
    }

    // Update local messages (always update regardless of review mode)
    const currentMessages = get().messages;
    set({ messages: [...currentMessages, messageData] });

    // Handle text-to-speech for assistant messages
    if (role === 'assistant' && type === 'text' && content && !useConfigStore.getState().learnTutorial) {
      useAudioStore.setState({ playingId: messageData.id });
      useAudioStore.setState({ playingType: 'assistant' });
      await playTextToSpeech(content);
    }
  },
  
  loadMessages: async (questId: string, chapterId: string, lessonId: string = 'empty', quest: Quest, mode: ModeType) => {
    try {
      // Then get user ID using our helper
      const user_id = useAuthStore.getState().user?.id;
      let messages: Message[] = [];
      
      if (user_id) {
        console.log("loading messages from supabase, user_id=", user_id, "questId=", questId, "chapterId=", chapterId, "lessonId=", lessonId, "mode=", mode);
        const { data, error } = await supabase
          .from('chat_message')
          .select('*')
          .eq('user_id', user_id)
          .eq('quest_id', questId)
          .eq('chapter_id', chapterId)
          .eq('lesson_id', lessonId)
          .eq('mode_type', mode)
          .order('created_at');
          
        if (error) {
          console.error('Error loading messages from Supabase:', error);
        } else if (data && data.length > 0) {
          messages = data as Message[];
          
          // Get all element messages
          const elementMessages = messages.filter(msg => msg.type === 'element' && msg.element_id);

          if (elementMessages.length > 0) {
            try {
              // Build element map from quest data (already contains correct elements via getLessonElements)
              // This avoids redundant database queries and properly handles dynamic lessons
              const elementMap: Record<string, Element> = {};
              quest.chapters.forEach(chapter => {
                chapter.lesson_list.forEach(lesson => {
                  lesson.elements.forEach(element => {
                    elementMap[element.id] = element;
                  });
                });
              });

              // Map elements back to messages and filter out messages with deleted elements
              elementMessages.forEach(msg => {
                if (msg.element_id && elementMap[msg.element_id]) {
                  msg.element = elementMap[msg.element_id];
                }
              });

              // Filter out messages that reference deleted elements
              messages = messages.filter(msg => {
                // Keep non-element messages
                if (msg.type !== 'element') {
                  return true;
                }
                // For element messages, only keep those that have valid element data
                return msg.element_id && elementMap[msg.element_id];
              });
            } catch (error) {
              console.error('Error mapping elements to messages:', error);
              // If mapping fails, filter out all element messages to avoid showing incomplete data
              messages = messages.filter(msg => msg.type !== 'element');
            }
          }
        }
      }
  
      console.log("messages=", messages);
      set({ currentQuestId: questId, currentChapterId: chapterId, currentLessonId: lessonId, messages: messages, mode: mode });
      
      // If no messages were loaded from the database, automatically add welcome message
      if (messages.length === 0) {
        console.log("No messages found, adding welcome message");
        
        // Get welcome message and quest data
        const questData = quest;
        const messageStore = get();
        
        // Find the first element to display
        let firstElement;
        if (questData && questData.chapters && questData.chapters.length > 0) {
          const visibleChapter = questData.chapters.find((chapter) => chapter.visible);

          // Find chapter id in questData.chapters
          const chapter = questData.chapters.find((ch: any) => ch.id === chapterId);
          // Find lesson id in chapter.lesson_list
          const lesson = chapter?.lesson_list.find((lesson: any) => lesson.id === lessonId);

          // Elements are already fetched via getLessonElements in getQuestById
          // Apply filtering based on mode if needed
          let elements = lesson?.elements || [];
          if (mode === ModeType.LEARN) {
            elements = elements.filter((element: Element) =>
              AccessElementTypes.includes(element.element_type as ElementType)
            );
          }

          firstElement = elements[0];
          
          let welcomeMessage = '';
          if (lesson?.welcome_message) {
            welcomeMessage = lesson.welcome_message;
          } else {
            welcomeMessage = await getWelcomeMessage(questId);
          }
          
          // Add welcome message
          await messageStore.addMessage({
            role: 'assistant',
            type: 'text',
            content: welcomeMessage,
          });
        }
        
        // Add first element if available
        if (firstElement) {
          // await messageStore.addMessage({
          //   role: 'assistant',
          //   type: 'text',
          //   content: firstElement.message_to_introduce || '',
          // });

          await messageStore.addMessage({
            role: 'assistant',
            type: 'element',
            element: firstElement,
          });

          if (firstElement?.element_type && InteractiveElementTypes.includes(firstElement?.element_type as ElementType)) {
            useChatStore.setState({ can_continue: false });
          };
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      set({ messages: [] });
    }
  },
  hasElementBeenShown: (elementId: string) => {
    const messages = get().messages;
    return messages.some(msg => msg.type === 'element' && msg.element_id === elementId);
  },
  
  isLastMessage: (messageId: string) => {
    const messages = get().messages;
    if (!messages.length || !messageId) return false;

    // Get the last message
    const lastMessage = messages[messages.length - 1];

    // Check if it matches the passed message ID
    return lastMessage.id === messageId;
  },

  // Clear messages and reinitialize (for review mode)
  clearAndReinitialize: async (
    questId: string,
    chapterId: string,
    lessonId: string,
    quest: Quest,
    mode: ModeType
  ) => {
    console.log('🔄 Clearing messages and reinitializing...');

    // 1. Clear current messages
    set({
      messages: [],
      currentQuestId: questId,
      currentChapterId: chapterId,
      currentLessonId: lessonId,
      mode: mode
    });

    // 2. Get lesson data
    const chapter = quest.chapters?.find((ch: any) => ch.id === chapterId);
    const lesson = chapter?.lesson_list?.find((l: any) => l.id === lessonId);

    if (!lesson) {
      console.error('Lesson not found for reinitialization');
      return;
    }

    // 3. Add welcome message
    if (lesson.welcome_message) {
      await get().addMessage({
        role: 'assistant',
        type: 'text',
        content: lesson.welcome_message,
      });
    } else {
      const welcomeMessage = await getWelcomeMessage(questId);
      await get().addMessage({
        role: 'assistant',
        type: 'text',
        content: welcomeMessage,
      });
    }

    // 4. Add first element
    let elements = lesson.elements || [];
    if (mode === ModeType.LEARN) {
      elements = elements.filter((element: Element) =>
        AccessElementTypes.includes(element.element_type as ElementType)
      );
    }

    const firstElement = elements[0];
    if (firstElement) {
      await get().addMessage({
        role: 'assistant',
        type: 'element',
        element: firstElement,
      });

      // If interactive element, set can_continue to false
      if (firstElement?.element_type &&
          InteractiveElementTypes.includes(firstElement.element_type as ElementType)) {
        useChatStore.setState({ can_continue: false });
      }
    }

    console.log('✅ Messages cleared and reinitialized');
  }
}));

// Helper function to handle text-to-speech
async function playTextToSpeech(content: string) {
  try {
    const cleanContent = stripNonTextCharacters(content);
    
    const audioStore = useAudioStore.getState();
    await audioStore.playTTS(cleanContent);
    // Set current playing audio type to assistant
    audioStore.setPlayingType('assistant');
    
    // Set the callback for when audio ends
    audioStore.setOnEnded(() => {
      const { messages, pendingMessages } = useMessageStore.getState();
      if (pendingMessages.length > 0) {
        useMessageStore.setState({ 
          messages: [...messages, ...pendingMessages],
          pendingMessages: []
        });
      }
    });
  } catch (error) {
    console.error('Error in TTS playback:', error);
  }
} 
